import { MapPin, Phone, Mail, LogIn, Shield, Award, CheckCircle2, ArrowRight } from 'lucide-react';
import { Logo } from '../ui/logo';
import { Link } from 'react-router-dom';
import { config } from '@/lib/config';
import { reportPhoneConversion } from '@/utils/analytics';

const locations = [
  {
    city: 'San Francisco',
    address: '595 Pacific Avenue',
    cityState: 'San Francisco, CA 94133'
  },
  {
    city: 'Walnut Creek',
    address: '1457 SOS Drive',
    cityState: 'Walnut Creek, CA 94597'
  }
];

export function Footer() {
  return (
    <footer 
      className="relative bg-gradient-to-br from-[#f1c232] via-[#e69138] to-[#d17f2a] pb-24 sm:pb-28 overflow-hidden"
      role="contentinfo"
      aria-label="Site footer"
    >
      {/* Decorative background elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
      </div>
      
      {/* Pattern overlay */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 bg-[linear-gradient(45deg,transparent_25%,rgba(255,255,255,0.1)_50%,transparent_75%,rgba(255,255,255,0.1)_100%)] bg-[length:20px_20px]"></div>
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="grid gap-12 lg:grid-cols-5">
          {/* Logo and Description */}
          <div className="lg:col-span-1">
            <div className="mb-6">
              <Logo variant="light" size="xl" />
            </div>
            <p className="mb-4 text-xs sm:text-sm leading-relaxed text-gray-900">
              Professional security solutions for residential and commercial properties.
              Protecting what matters most since 1985.
            </p>
            
            {/* License Badges */}
            <div className="flex flex-wrap gap-1.5">
              <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white/70 backdrop-blur-sm border border-white/40 text-xs">
                <Shield className="h-3 w-3 text-[#990000]" />
                <span className="font-semibold text-[#990000]">CSLB: 681875-C10</span>
              </div>
              <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white/70 backdrop-blur-sm border border-white/40 text-xs">
                <Award className="h-3 w-3 text-[#990000]" />
                <span className="font-semibold text-[#990000]">ACO-2160</span>
              </div>
              <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white/70 backdrop-blur-sm border border-white/40 text-xs">
                <CheckCircle2 className="h-3 w-3 text-[#990000]" />
                <span className="font-semibold text-[#990000]">UL Listed</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div className="lg:col-span-1">
            <h3 className="mb-4 text-sm font-semibold text-gray-900 uppercase tracking-wide">Quick Links</h3>
            <div className="space-y-2">
              <Link 
                to="/services" 
                className="group flex items-center gap-1.5 text-sm text-gray-900 hover:text-[#990000] transition-colors duration-200"
              >
                <ArrowRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                <span>Services</span>
              </Link>
              <Link 
                to="/about" 
                className="group flex items-center gap-1.5 text-xs sm:text-sm text-gray-900 hover:text-[#990000] transition-colors duration-200"
              >
                <ArrowRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                <span>About Us</span>
              </Link>
              <Link 
                to="/blog" 
                className="group flex items-center gap-1.5 text-xs sm:text-sm text-gray-900 hover:text-[#990000] transition-colors duration-200"
              >
                <ArrowRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                <span>Blog</span>
              </Link>
              <Link 
                to="/resources" 
                className="group flex items-center gap-1.5 text-xs sm:text-sm text-gray-900 hover:text-[#990000] transition-colors duration-200"
              >
                <ArrowRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                <span>Resources</span>
              </Link>
              <Link 
                to="/support" 
                className="group flex items-center gap-1.5 text-xs sm:text-sm text-gray-900 hover:text-[#990000] transition-colors duration-200"
              >
                <ArrowRight className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                <span>Support</span>
              </Link>
            </div>
          </div>

          {/* Locations */}
          <div className="lg:col-span-2">
            <h3 className="mb-4 text-sm font-semibold text-gray-900 uppercase tracking-wide">Our Locations</h3>
            <div className="grid gap-3 sm:grid-cols-2">
              {locations.map((location) => (
                <div 
                  key={location.city} 
                  className="group flex items-start gap-3 rounded-lg bg-white/60 backdrop-blur-sm p-3 border border-white/40 hover:bg-white/80 hover:border-white/60 transition-all duration-200"
                >
                  <div className="flex-shrink-0 mt-0.5">
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-[#990000] to-[#e69138]">
                      <MapPin className="h-4 w-4 text-white" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="mb-1 text-xs sm:text-sm font-semibold text-gray-900 group-hover:text-[#990000] transition-colors">
                      {location.city}
                    </h4>
                    <p className="text-[10px] sm:text-xs leading-relaxed text-gray-800">
                      {location.address}<br />
                      {location.cityState}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Contact Info */}
          <div className="lg:col-span-1">
            <h3 className="mb-4 text-xs sm:text-sm font-semibold text-gray-900 uppercase tracking-wide">Contact Us</h3>
            <div className="space-y-3">
              <a
                href="tel:8005402140"
                onClick={(event) => {
                  event.preventDefault();
                  reportPhoneConversion();
                }}
                className="group flex items-center gap-3 rounded-lg bg-white/60 backdrop-blur-sm p-3 border border-white/40 hover:bg-white/80 hover:border-white/60 transition-all duration-200"
              >
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-[#990000] to-[#e69138] flex-shrink-0">
                  <Phone className="h-4 w-4 text-white" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] sm:text-xs font-medium text-gray-900 group-hover:text-[#990000] transition-colors">
                    (800) 540-2140
                  </p>
                </div>
              </a>
              
              <a 
                href="mailto:info@securityengineers.com" 
                className="group flex items-center gap-3 rounded-lg bg-white/60 backdrop-blur-sm p-3 border border-white/40 hover:bg-white/80 hover:border-white/60 transition-all duration-200"
              >
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-[#990000] to-[#e69138] flex-shrink-0">
                  <Mail className="h-4 w-4 text-white" />
                </div>
                <div className="min-w-0">
                  <p className="text-[10px] sm:text-xs font-medium text-gray-900 group-hover:text-[#990000] transition-colors break-all">
                    info@securityengineers.com
                  </p>
                </div>
              </a>
              
              {config.fieldVeroUrl !== '#' ? (
                <a
                  href={config.fieldVeroUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-3 rounded-lg bg-white/60 backdrop-blur-sm p-3 border border-white/40 hover:bg-white/80 hover:border-white/60 transition-all duration-200"
                >
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-[#990000] to-[#e69138] flex-shrink-0">
                    <LogIn className="h-4 w-4 text-white" />
                  </div>
                  <div className="min-w-0">
                  <p className="text-[10px] sm:text-xs font-medium text-gray-900 group-hover:text-[#990000] transition-colors">
                    Client Portal
                  </p>
                  </div>
                </a>
              ) : (
                <div className="flex items-center gap-3 rounded-lg bg-gray-100/40 backdrop-blur-sm p-3 border border-gray-200/40 opacity-60">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gray-300 flex-shrink-0">
                    <LogIn className="h-4 w-4 text-gray-500" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-gray-500">
                      Client Portal
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="mt-8 pt-6 border-t border-white/30">
          <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
            <div className="flex flex-col items-center sm:items-start gap-2">
              <p className="text-[10px] sm:text-xs text-gray-900">
                © {new Date().getFullYear()} Security Engineers. All rights reserved.
              </p>
              <div className="flex flex-wrap items-center gap-1.5 text-xs text-gray-800">
                <span className="px-1.5 py-0.5 rounded bg-white/60 backdrop-blur-sm border border-white/40">CSLB: 681875-C10</span>
                <span className="px-1.5 py-0.5 rounded bg-white/60 backdrop-blur-sm border border-white/40">ACO-2160</span>
                <span className="px-1.5 py-0.5 rounded bg-white/60 backdrop-blur-sm border border-white/40">UL Listed</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link 
                to="/privacy" 
                className="text-[10px] sm:text-xs text-gray-900 hover:text-[#990000] transition-colors duration-200"
              >
                Privacy Policy
              </Link>
              <Link 
                to="/cookies" 
                className="text-[10px] sm:text-xs text-gray-900 hover:text-[#990000] transition-colors duration-200"
              >
                Cookie Policy
              </Link>
              <Link 
                to="/terms" 
                className="text-[10px] sm:text-xs text-gray-900 hover:text-[#990000] transition-colors duration-200"
              >
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
